import { Fragment, useEffect } from 'react';

import { getVendorType } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import type { WCoupleContextView } from '@zola/svc-web-api-ts-client';
import LinkV2 from '@zola/zola-ui/src/components/LinkV2/LinkV2';
import { useForceUpdateOnWindowResize } from '@zola/zola-ui/src/hooks';
import { COLORS3, MEDIA_QUERY, ZINDEX } from '@zola/zola-ui/src/styles/emotion';
import MEDIA_QUERY_V2 from '@zola/zola-ui/src/styles/emotion/mediaQueryV2';

import styled from '@emotion/styled';
import { CamelCasedPropertiesDeep } from 'type-fest';

import { receivedBulkInquiryToStorefront } from '~/actions/types/couplesInquiriesActionTypes';
import ClientApp from '~/ClientApp';
import ClientRouter from '~/ClientRouter';
import { useUserContext } from '~/contexts/UserContext';
import { VendorSearchNavigationProvider } from '~/pages/couples/explore/components/SearchResults/contexts/VendorSearchNavigationProvider';
import { useAppDispatch } from '~/reducers';
import ApiService from '~/util/apiService';

import SearchResultsPageModalsAndToasts from '../../SearchResultsPageModalsAndToasts';
import { ByStyleCategoryCarouselForSRP } from '../ByStyleCategoryCarousel/ByStyleCategoryCarouselForSrp';
import { ExpertAdviceForSRP } from '../ExpertAdvice/ExpertAdviceForSrp';
import { PageSection } from '../PageSection/PageSection';
import { AdjacentVendors } from './components/AdjacentVendors/AdjacentVendors';
import { MobileSearchFilters } from './components/PreAuth/MobileSearchFilters';
import { PageHeading } from './components/PreAuth/PageHeading';
import { PreAuthSearchResults } from './components/PreAuth/PreAuthSearchResults';
import { ZolaTopPicks } from './components/PreAuth/ZolaTopPicks';
import { RealWeddings } from './components/RealWeddings/RealWeddings';
import {
  SmallVendorCardList,
  SCRIM_WIDTH_DESKTOP,
  LeftScrim,
} from './components/RecentlyViewedStorefronts/recentlyViewedStorefronts.styles';
import {
  SearchBreadcrumb,
  useSearchBreadcrumbs,
} from './components/SearchBreadcrumb/SearchBreadcrumb';
import { SRPSearchBarProvider } from './components/SearchControls/SRPSearchBarContext';
import { SearchResultsHead } from './components/SearchResultsHead/SearchResultsHead';
import { SeoContentBlock } from './components/SeoContentBlock/SeoContentBlock';
import { SeoCrossLinks } from './components/SeoCrossLinks/SeoCrossLinks';
import { useSearchResultsPageContext } from './contexts/SearchResultsPageContext';
import { VendorGridCardsProvider } from './contexts/VendorGridCardsProvider';

import styles from './preAuthSearchResultsPage.module.less';

const MobileWebBreadcrumb = () => {
  const breadcrumbList = useSearchBreadcrumbs();

  if (breadcrumbList.length < 2) {
    return <Fragment />;
  }

  const item = breadcrumbList[breadcrumbList.length - 2];

  return (
    <nav
      style={{ paddingTop: '16px', paddingLeft: '16px', paddingRight: '16px' }}
      className={styles.onlyMobile}
    >
      <ul>
        <li>
          <LinkV2 className={styles.backLink} arrow arrowPosition="prefix" href={item.to}>
            {item.label}
          </LinkV2>
        </li>
      </ul>
    </nav>
  );
};

const Breadcrumb = () => {
  return (
    <PageSection noPadding stretchToEdge>
      <MobileWebBreadcrumb />
      <div className={styles.desktopBreadcrumb}>
        <SearchBreadcrumb />
      </div>
    </PageSection>
  );
};

const ScrollTop = () => {
  const { canonicalUrl } = useSearchResultsPageContext();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [canonicalUrl]);

  return <Fragment />;
};

const DESKTOP_GUTTER = 40;

// TODO: We want this to be 40
const TABLET_GUTTER = 16;
const MOBILE_GUTTER = 16;

const ZolaTopPicksWrapper = styled.div`
  background-color: ${COLORS3.CLOUD_100};
  ${LeftScrim} {
    ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
      width: 16px;
    }
  }

  ${SmallVendorCardList} {
    margin-right: -${SCRIM_WIDTH_DESKTOP}px;
  }

  .zola-ui.tab-component {
    // Styles for the tabs at the top
    .tabs-list-container {
      margin: 0;
      // Styles for a tab
      .tab {
        font-size: 14px;
        line-height: 20px;

        &:first-child {
          margin-left: ${DESKTOP_GUTTER}px;

          ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
            margin-left: ${MOBILE_GUTTER}px;
          }

          ${MEDIA_QUERY.MOBILE} {
            // override zola-ui style
            padding-left: 0;

            &::before {
              // override zola-ui style
              left: 0;
            }
          }
        }

        &:last-child {
          margin-right: ${DESKTOP_GUTTER}px;
        }
      }

      // Styles for the scrim on the tabs
      .fade-edges {
        // This is a overriding from tab.less in zola-ui
        &::before {
          position: absolute;
          content: '';
          z-index: ${ZINDEX.top};
          // Gradient going to the left starting at transparent white (255, 255, 255, 0) and
          // ending with CLOUD_100, 100% opaque (249, 249, 249, 1).
          background: linear-gradient(
            to left,
            rgba(255, 255, 255, 0) 0%,
            rgba(249, 249, 249, 1) 100%
          );
          top: 0; // scrim starts at the top-left
          left: 0; // scrim starts at the top-left
          bottom: 0; // goes to the bottom, including the 4px border for the current item
          width: ${DESKTOP_GUTTER}px; // how wide is the scrim
          height: 52px; // match the height of the tab item in zola ui, but not the 1px border under the tabs

          ${MEDIA_QUERY_V2.TABLET} {
            width: ${TABLET_GUTTER}px;
          }
          ${MEDIA_QUERY.MOBILE} {
            // override selectors in zola-ui
            bottom: 0;
            height: 52px;

            background: linear-gradient(
              to left,
              rgba(255, 255, 255, 0) 0%,
              rgba(249, 249, 249, 1) 100%
            );

            width: 16px;
          }
        }
        &::after {
          position: absolute;
          content: '';
          z-index: ${ZINDEX.top};
          // Gradient going to the right starting at transparent white (255, 255, 255, 0) and
          // ending with CLOUD_100, 100% opaque (249, 249, 249, 1).
          background: linear-gradient(
            to right,
            rgba(255, 255, 255, 0) 0%,
            rgba(249, 249, 249, 1) 100%
          );
          top: 0; // top-right
          right: 0; // top-right
          bottom: 0; // to bottom
          width: ${DESKTOP_GUTTER}px; // changed from 20px to 40px
          height: 52px;

          ${MEDIA_QUERY.MOBILE} {
            // override selectors in zola-ui
            bottom: 0;
            height: 52px;

            background: linear-gradient(
              to right,
              rgba(255, 255, 255, 0) 0%,
              rgba(249, 249, 249, 1) 100%
            );

            // See comment in last-of-type selector above, we use a wider scrim on the right
            width: ${DESKTOP_GUTTER}px;
          }
        }
      }
    }

    // styles for the tab content
    .tab-panels {
      .panel-content {
        ${MEDIA_QUERY.MOBILE} {
          // override 20px margin on mobile so our content pushes to the edges so
          // our scrim works
          margin: 0;
        }
      }
    }
  }
`;

/**
 * Pre-auth winner of an SRP A/B test variation:
 * - Grid of cards to list of cards
 * - Card presentation is wider, includes a review and a vendor description
 * - Location of favorite button is moved
 * - On desktop, a left rail of filter controls is used
 * - H1 header is changed
 *
 * Control was vs a grid of cards
 */
export const PreAuthSearchResultsPage = () => {
  const userContext = useUserContext();
  const { vendorSearch } = useSearchResultsPageContext();
  const dispatch = useAppDispatch();

  useForceUpdateOnWindowResize();

  // If the page becomes a post-auth page, fetch inquiries
  useEffect(() => {
    if (userContext && !userContext.is_guest) {
      const params = {
        inquiryRequest: {
          vendor_type: vendorSearch.selectedTaxonomy?.key
            ? getVendorType(vendorSearch.selectedTaxonomy?.key)
            : undefined,
          limit: 500,
        },
      };

      ApiService.post<CamelCasedPropertiesDeep<WCoupleContextView>>(
        `/web-marketplace-api/v1/marketplace-context/for-couple`,
        params
      )
        .then((response) => {
          const { storefrontUuidToInquiryUuidMap } = response;

          if (storefrontUuidToInquiryUuidMap) {
            dispatch(receivedBulkInquiryToStorefront(storefrontUuidToInquiryUuidMap));
          }
        })
        .catch(() => null);
    }
  }, [dispatch, userContext, vendorSearch.selectedTaxonomy?.key]);

  return (
    <div className={styles.pageWrapper}>
      <ScrollTop />
      <VendorSearchNavigationProvider>
        <SearchResultsHead isPreAuthSrp />
        <Breadcrumb />
        <SRPSearchBarProvider>
          <PageHeading />
        </SRPSearchBarProvider>
        <div className={styles.divider}>
          <hr />
        </div>

        <MobileSearchFilters />
        <ZolaTopPicks tabWrapper={ZolaTopPicksWrapper}>
          <div className={styles.divider}>
            <hr />
          </div>
        </ZolaTopPicks>

        <VendorGridCardsProvider>
          <PreAuthSearchResults />
        </VendorGridCardsProvider>
        <ByStyleCategoryCarouselForSRP className={styles.withTopBorder} />
        <AdjacentVendors className={styles.withTopBorder} />
        <ExpertAdviceForSRP className={styles.withTopBorder} />
        <RealWeddings className={styles.withTopBorder} />
        <SeoContentBlock className={styles.withTopBorder} />
        <SeoCrossLinks className={styles.withTopBorder} />
      </VendorSearchNavigationProvider>

      <ClientApp useStore={false}>
        <ClientRouter>
          <SearchResultsPageModalsAndToasts />
        </ClientRouter>
      </ClientApp>
    </div>
  );
};
